// FirewallVpnPhase2.tsx
import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, Loader2, Search, GitBranch,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowUp, ArrowDown, ArrowLeft,
    Network, Lock, Clock, Shield
} from "lucide-react";
import { showError } from "../../../../../utils/toast";
import baseapi from "../../../../../services/baseapi";
import { ItemsPerPageDropdown } from "../../../../../components/ItemsPerPageDropdown ";


// ── Types ─────────────────────────────────────────────────────────────────────
interface Phase2Item {
    id: number;
    created: string;
    updated: string;
    asset_id: number;
    firewall_type: string;
    name: string;
    phase1name: string;
    proposal: string;
    pfs: string;
    dhgrp: string;
    replay: string;
    keepalive: string;
    auto_negotiate: string;
    keylifeseconds: number;
    keylifekbs: number;
    keylife_type: string;
    encapsulation: string;
    protocol: string;
    src_name: string;
    src_subnet: string;
    src_port: string;
    dst_name: string;
    dst_subnet: string;
    dst_port: string;
    comments: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtKeylife = (secs: number): string => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
};

// ── Primitives ────────────────────────────────────────────────────────────────
const Chip = ({ label, color }: { label: string; color: string }) => (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase whitespace-nowrap ${color}`}>
        {label}
    </span>
);

const enabledColor = (v: string) =>
    v === "enable"
        ? "bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/20 text-green-700 dark:text-green-400"
        : "bg-slate-100 dark:bg-gray-700/40 border-slate-200 dark:border-gray-600/30 text-slate-500 dark:text-gray-500";


const SortIcon = ({ column, sortConfig }: {
    column: string;
    sortConfig: { key: string; direction: "asc" | "desc" } | null;
}) => {
    if (sortConfig?.key !== column) return (
        <div className="opacity-0 group-hover:opacity-40 transition-opacity">
            <ArrowUp size={10} className="text-slate-400" />
        </div>
    );
    return sortConfig.direction === "asc"
        ? <ArrowUp size={10} className="text-blue-600 dark:text-blue-400" />
        : <ArrowDown size={10} className="text-blue-600 dark:text-blue-400" />;
};

const renderDetailRow = (label: string, value: any, color = "text-slate-800 dark:text-slate-200") => (
    <div key={label} className="flex justify-between items-center py-1 border-b border-slate-100 dark:border-gray-700/40 last:border-0 hover:bg-slate-50 dark:hover:bg-gray-700/20 px-1 -mx-1 rounded transition-colors">
        <span className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest">{label}</span>
        <span className={`text-[10px] font-black ${color} text-right max-w-[65%] truncate`} title={String(value ?? "")}>
            {value !== null && value !== undefined && value !== "" ? String(value) : "—"}
        </span>
    </div>
);

// ── Detail View ───────────────────────────────────────────────────────────────
function Phase2Detail({ item, onBack }: { item: Phase2Item; onBack: () => void }) {
    return (
        <div className="space-y-3 animate-fade-in">

            {/* Header */}
            <div className="flex items-center gap-3 bg-slate-50 dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 p-2.5 rounded-xl shadow-sm">
                <button
                    onClick={onBack}
                    className="p-1.5 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-md text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-white hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-all group shrink-0"
                >
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                </button>
                <div className="bg-blue-100 dark:bg-blue-500/10 p-1.5 rounded-md border border-blue-200 dark:border-blue-500/20 shrink-0">
                    <GitBranch className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-sm font-bold text-slate-900 dark:text-white">{item.name}</h2>
                    <Chip label={item.encapsulation} color="bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/20 text-blue-700 dark:text-blue-400" />
                    <Chip label={`PFS: ${item.pfs}`} color={enabledColor(item.pfs)} />
                    <Chip label={item.keylife_type} color="bg-slate-100 dark:bg-gray-700/50 border-slate-200 dark:border-gray-600/40 text-slate-600 dark:text-gray-400" />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                {/* Crypto */}
                <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 p-3 rounded-lg shadow-sm">
                    <div className="flex items-center gap-1.5 mb-2 border-b border-slate-100 dark:border-gray-700 pb-1.5">
                        <Lock className="w-3.5 h-3.5 text-amber-600 dark:text-yellow-400" />
                        <h4 className="text-[10px] font-black text-amber-600 dark:text-yellow-400 uppercase tracking-widest">Cryptography</h4>
                    </div>
                    <div className="space-y-0.5">
                        {renderDetailRow("Proposal", item.proposal, "text-amber-600 dark:text-yellow-300")}
                        {renderDetailRow("DH Groups", item.dhgrp || null)}
                        {renderDetailRow("PFS", item.pfs, item.pfs === "enable" ? "text-emerald-600 dark:text-green-400" : "text-red-600 dark:text-red-400")}
                        {renderDetailRow("Replay Protection", item.replay, item.replay === "enable" ? "text-emerald-600 dark:text-green-400" : "text-red-600 dark:text-red-400")}
                        {renderDetailRow("Encapsulation", item.encapsulation)}
                        {renderDetailRow("Protocol", item.protocol || null)}
                    </div>
                </div>

                {/* Keylife & Timers */}
                <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 p-3 rounded-lg shadow-sm">
                    <div className="flex items-center gap-1.5 mb-2 border-b border-slate-100 dark:border-gray-700 pb-1.5">
                        <Clock className="w-3.5 h-3.5 text-emerald-600 dark:text-green-400" />
                        <h4 className="text-[10px] font-black text-emerald-600 dark:text-green-400 uppercase tracking-widest">Key Lifetime</h4>
                    </div>
                    <div className="space-y-0.5">
                        {renderDetailRow("Key Life Type", item.keylife_type)}
                        {renderDetailRow("Key Life (sec)", `${item.keylifeseconds}s (${fmtKeylife(item.keylifeseconds)})`)}
                        {renderDetailRow("Key Life (KB)", `${item.keylifekbs} KB`)}
                        {renderDetailRow("Keepalive", item.keepalive, item.keepalive === "enable" ? "text-emerald-600 dark:text-green-400" : "text-slate-400 dark:text-gray-500")}
                        {renderDetailRow("Auto Negotiate", item.auto_negotiate, item.auto_negotiate === "enable" ? "text-emerald-600 dark:text-green-400" : "text-slate-400 dark:text-gray-500")}
                        {renderDetailRow("Phase 1 Ref", item.phase1name, "text-purple-600 dark:text-purple-400")}
                    </div>
                </div>

                {/* Subnets */}
                <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 p-3 rounded-lg shadow-sm">
                    <div className="flex items-center gap-1.5 mb-2 border-b border-slate-100 dark:border-gray-700 pb-1.5">
                        <Network className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
                        <h4 className="text-[10px] font-black text-cyan-600 dark:text-cyan-400 uppercase tracking-widest">Subnets</h4>
                    </div>
                    <div className="space-y-3">
                        <div>
                            <p className="text-[9px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-1.5">Source</p>
                            <p className="font-mono text-[11px] text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-500/5 border border-blue-200 dark:border-blue-500/10 px-2 py-1 rounded">
                                {item.src_subnet || item.src_name || "—"}
                            </p>
                            {item.src_port && <p className="text-[9px] text-slate-500 dark:text-gray-500 mt-1">Port: {item.src_port}</p>}
                        </div>
                        <div className="flex justify-center">
                            <ArrowDown size={14} className="text-slate-400 dark:text-gray-600" />
                        </div>
                        <div>
                            <p className="text-[9px] font-bold text-orange-600 dark:text-orange-400 uppercase tracking-widest mb-1.5">Destination</p>
                            <p className="font-mono text-[11px] text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-500/5 border border-orange-200 dark:border-orange-500/10 px-2 py-1 rounded">
                                {item.dst_subnet || item.dst_name || "—"}
                            </p>
                            {item.dst_port && <p className="text-[9px] text-slate-500 dark:text-gray-500 mt-1">Port: {item.dst_port}</p>}
                        </div>
                    </div>
                </div>
            </div>

            {item.comments && (
                <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 p-3 rounded-lg shadow-sm">
                    <p className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-1">Comments</p>
                    <p className="text-[10px] text-slate-700 dark:text-gray-300">{item.comments}</p>
                </div>
            )}
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function FirewallVpnPhase2({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [items, setItems] = useState<Phase2Item[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selected, setSelected] = useState<Phase2Item | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;
            if (client_id) queryObj["client_id"] = client_id;
            if (debouncedSearch) queryObj.$or = [
                { name: { $ilike: `%${debouncedSearch}%` } },
                { phase1name: { $ilike: `%${debouncedSearch}%` } }
            ];
            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * limit,
                limit
            };
            if (sortConfig) { params.sort_by = sortConfig.key; params.sort_order = sortConfig.direction; }
            const res = await baseapi.get("/firewall_vpn_phase2", { params });
            if (res.data.status) {
                setItems(res.data.data);
                setTotalRecords(res.data.total ?? res.data.data.length);
            } else showError("Failed to load Phase 2 data");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id, debouncedSearch, currentPage, limit, sortConfig]);

    useEffect(() => { if (!selected) fetchData(); }, [fetchData, selected]);

    const handleSort = (key: string) => {
        setSortConfig(prev =>
            prev?.key === key
                ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
                : { key, direction: "asc" }
        );
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalRecords / limit);
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    if (selected) return <Phase2Detail item={selected} onBack={() => setSelected(null)} />;

    const Th = ({ label, col, right = false }: { label: string; col: string; right?: boolean }) => (
        <th
            className={`px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap ${right ? "text-right" : ""}`}
            onClick={() => handleSort(col)}
        >
            <div className={`flex items-center gap-1 ${right ? "justify-end" : ""}`}>
                {label} <SortIcon column={col} sortConfig={sortConfig} />
            </div>
        </th>
    );

    return (
        <div className="flex flex-col bg-transparent text-slate-800 dark:text-gray-200">

            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-2">
                <div className="flex items-center gap-2">
                    <div className="bg-blue-100 dark:bg-blue-600/20 p-1.5 rounded-lg border border-blue-200 dark:border-blue-500/30">
                        <GitBranch size={16} className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <h1 className="text-lg font-bold text-slate-900 dark:text-white tracking-wide">Phase 2</h1>
                    <span className="text-[10px] font-bold text-slate-600 dark:text-gray-400 bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 px-2 py-0.5 rounded">
                        {totalRecords} entries
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative w-56 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 dark:text-gray-500 group-focus-within:text-blue-600 dark:group-focus-within:text-blue-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search Phase 2..."
                            className="w-full bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-all shadow-sm"
                        />
                    </div>
                    <button
                        onClick={fetchData}
                        disabled={loading}
                        className="p-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-gray-700 transition-all group shrink-0 shadow-sm"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-blue-600 dark:group-hover:text-blue-400"} />
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-slate-50 dark:bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-slate-200 dark:border-gray-700/50">
                                <Th label="Name" col="name" />
                                <Th label="Phase 1" col="phase1name" />
                                <Th label="Proposal" col="proposal" />
                                <Th label="Src Subnet" col="src_subnet" />
                                <Th label="Dst Subnet" col="dst_subnet" />
                                <Th label="Encapsulation" col="encapsulation" />
                                <Th label="PFS" col="pfs" />
                                <Th label="DH Group" col="dhgrp" />
                                <Th label="Key Life" col="keylifeseconds" right />
                                <Th label="Replay" col="replay" />
                                <Th label="Auto Neg" col="auto_negotiate" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={11} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-gray-400">
                                            <Loader2 className="w-4 h-4 animate-spin text-blue-600 dark:text-blue-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600 dark:text-gray-300">Loading Phase 2...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={11} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-60">
                                            <GitBranch size={24} className="text-slate-400 dark:text-gray-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-400">No Phase 2 entries found</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                items.map((item) => (
                                    <tr
                                        key={item.id}
                                        onClick={() => setSelected(item)}
                                        className="hover:bg-slate-50/80 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                                        title="Click to view details"
                                    >
                                        {/* Name */}
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <div>
                                                    <p className="text-[11px] font-bold text-slate-900 dark:text-gray-100">{item.name.replace(/_/g, " ")}</p>
                                                    {item.comments && (
                                                        <p className="text-[9px] text-slate-500 dark:text-gray-400 truncate max-w-[130px]">{item.comments}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>

                                        {/* Phase 1 ref */}
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border bg-purple-50 dark:bg-purple-500/10 border-purple-200 dark:border-purple-500/20 text-purple-700 dark:text-purple-400">
                                                <Shield size={8} />
                                                {item.phase1name}
                                            </span>
                                        </td>

                                        {/* Proposal */}
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <span className="font-mono text-[10px] text-amber-700 dark:text-yellow-300/80">{item.proposal || "—"}</span>
                                        </td>

                                        {/* Src Subnet */}
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <span className="font-mono text-[10px] text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-500/5 border border-blue-200 dark:border-blue-500/10 px-1.5 py-0.5 rounded">
                                                {item.src_subnet || item.src_name || "—"}
                                            </span>
                                        </td>

                                        {/* Dst Subnet */}
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <span className="font-mono text-[10px] text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-500/5 border border-orange-200 dark:border-orange-500/10 px-1.5 py-0.5 rounded">
                                                {item.dst_subnet || item.dst_name || "—"}
                                            </span>
                                        </td>

                                        {/* Encapsulation */}
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <Chip label={item.encapsulation} color="bg-cyan-50 dark:bg-cyan-500/10 border-cyan-200 dark:border-cyan-500/20 text-cyan-700 dark:text-cyan-400" />
                                        </td>

                                        {/* PFS */}
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <Chip label={item.pfs} color={enabledColor(item.pfs)} />
                                        </td>

                                        {/* DH Group */}
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <span className="font-mono text-[10px] text-slate-700 dark:text-gray-300">
                                                {item.dhgrp
                                                    ? item.dhgrp.trim().split(/\s+/).map(g => `grp${g}`).join(", ")
                                                    : "—"}
                                            </span>
                                        </td>

                                        {/* Key Life */}
                                        <td className="px-4 py-2.5 whitespace-nowrap text-right">
                                            <div className="flex flex-col items-end gap-0.5">
                                                <span className="text-[11px] font-mono text-slate-800 dark:text-gray-200">{fmtKeylife(item.keylifeseconds)}</span>
                                                <span className="text-[9px] text-slate-400 dark:text-gray-500">{item.keylifeseconds}s</span>
                                            </div>
                                        </td>

                                        {/* Replay */}
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <Chip label={item.replay} color={enabledColor(item.replay)} />
                                        </td>

                                        {/* Auto Negotiate */}
                                        <td className="px-4 py-2.5 whitespace-nowrap">
                                            <Chip label={item.auto_negotiate} color={enabledColor(item.auto_negotiate)} />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 dark:bg-gray-800/40 border-t border-slate-200 dark:border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-slate-500 dark:text-gray-400 tracking-widest">
                            <span className="text-slate-700 dark:text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-slate-700 dark:text-gray-300">{endRecord}</span>
                            <span className="mx-1 text-slate-400 dark:text-gray-600">/</span>
                            <span className="text-slate-800 dark:text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {debouncedSearch && (
                            <button onClick={() => setSearchTerm("")}
                                className="text-[9px] text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-bold uppercase tracking-widest transition-colors">
                                Clear search
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}
                            className="p-1 text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded">
                            <ChevronsLeft size={14} />
                        </button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded">
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 text-blue-700 dark:text-blue-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded">
                            <ChevronRight size={14} />
                        </button>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded">
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
